import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Form = () => {
  const [manualUpload, setManualUpload] = useState({
    supplier: "",
    inspectorName: "",
    date: "",
    shiftType: "",
    shift: "",
    manufacturingLine: "",
    modulePowerRating: "",
    startSerial: { prefix: "", number: "", suffix: "" },
    endSerial: { prefix: "", number: "", suffix: "" }
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [shiftOptions, setShiftOptions] = useState([]);
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    // Prefill inspector name from localStorage
    const username = sessionStorage.getItem('username');
    if (username) {
      setManualUpload(prev => ({ ...prev, inspectorName: username }));
    }

    const fetchSuppliers = async () => {
      try {
        const selectedProject = localStorage.getItem('selectedProject');
        const response = await axios.post(
          `${baseURL}get_supplier_names`,
          { projectId: selectedProject }
        );
        setSuppliers(response.data);
      } catch (error) {
        console.error('Failed to fetch suppliers', error);
      }
    };

    fetchSuppliers();
  }, []);

  const handleSupplierChange = (e) => {
    const selectedSupplier = e.target.value;
    setManualUpload(prev => ({ ...prev, supplier: selectedSupplier, shift: "", shiftType: "" }));

    // Find the shift options based on the selected supplier's name
    const supplier = suppliers.find(sup => sup.name === selectedSupplier);
    if (supplier) {
      const shiftType = supplier.shift;
      setManualUpload(prev => ({ ...prev, shiftType }));
      setShiftOptions(shiftType === "DayNight" ? ["Day", "Night"] : ["Morning", "Afternoon", "Night"]);
    }
  };

  const handleChange = (e, type = null, part = null) => {
    const { name, value } = e.target;
    setManualUpload(prev => type
      ? { ...prev, [type]: { ...prev[type], [part]: value } }
      : { ...prev, [name]: value }
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Manual Upload Json:', JSON.stringify(manualUpload, null, 2));
    setIsSubmitted(true);

    setManualUpload({
      supplier: "",
      inspectorName: sessionStorage.getItem('username') || "",
      date: "",
      shiftType: "",
      shift: "",
      manufacturingLine: "",
      modulePowerRating: "",
      startSerial: { prefix: "", number: "", suffix: "" },
      endSerial: { prefix: "", number: "", suffix: "" }
    });

    setTimeout(() => setIsSubmitted(false), 3000);
  };

  return (
    <div className="p-6 md:p-10 rounded-lg shadow-lg bg-white w-full mx-auto mt-5">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6 text-center">Daily Layup Information</h2>

      {isSubmitted && (
        <div className="bg-green-100 border-t-4 border-green-500 rounded-b text-green-700 px-4 py-3 shadow-md mb-4" role="alert">
          <p className="font-bold">Success</p>
          <p className="text-sm">Form submitted successfully!</p>
        </div>
      )}

      <form className="grid grid-cols-1 md:grid-cols-2 gap-6" onSubmit={handleSubmit}>
        <div>
          <label className="block text-gray-700 text-sm font-semibold mb-2">Supplier</label>
          <select
            name="supplier"
            value={manualUpload.supplier}
            className="appearance-none rounded-lg w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-customBlue focus:ring-2 bg-gray-100 border border-gray-300"
            onChange={handleSupplierChange}
          >
            <option value="">Select Supplier</option>
            {suppliers.map(supplier => (
              <option key={supplier.name} value={supplier.name}>{supplier.name}</option>
            ))}
          </select>
        </div>

        {[
          { label: 'Inspector Name', name: 'inspectorName', type: 'text', placeholder: 'Enter Name of inspector' },
          { label: 'Date', name: 'date', type: 'date' },
          { label: 'Manufacturing Line', name: 'manufacturingLine', type: 'text', placeholder: 'Enter Manufacturing Line' },
          { label: 'Module Power Rating', name: 'modulePowerRating', type: 'text', placeholder: 'Enter Module Power Rating' }
        ].map(({ label, name, type, placeholder }) => (
          <div key={name}>
            <label className="block text-gray-700 text-sm font-semibold mb-2">{label}</label>
            <input
              type={type}
              name={name}
              value={manualUpload[name]}
              placeholder={placeholder}
              className="appearance-none rounded-lg w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-customBlue focus:ring-2 bg-gray-100 border border-gray-300"
              onChange={handleChange}
            />
          </div>
        ))}

        <div>
          <label className="block text-gray-700 text-sm font-semibold mb-2">Shift</label>
          <select
            name="shift"
            value={manualUpload.shift}
            className="appearance-none rounded-lg w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-customBlue focus:ring-2 bg-gray-100 border border-gray-300"
            onChange={handleChange}
            disabled={!manualUpload.supplier || !shiftOptions.length} // Disable when no supplier is selected
          >
            <option value="">Select Shift</option>
            {shiftOptions.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>

        {['Start', 'End'].map((label) => (
          <div key={label}>
            <label className="block text-gray-700 text-sm font-semibold mb-2">{`${label} Serial No`}</label>
            <div className="flex">
              {['prefix', 'number', 'suffix'].map(part => (
                <input
                  key={part}
                  type={part === 'number' ? 'number' : 'text'}
                  placeholder={part.charAt(0).toUpperCase() + part.slice(1)}
                  className={`appearance-none ${part === 'prefix' ? 'rounded-l-lg' : part === 'suffix' ? 'rounded-r-lg' : ''} w-1/3 py-2 px-4 text-gray-700 leading-tight focus:outline-none bg-gray-100 border border-gray-300`}
                  value={manualUpload[`${label.toLowerCase()}Serial`][part]}
                  onChange={(e) => handleChange(e, `${label.toLowerCase()}Serial`, part)}
                />
              ))}
            </div>
          </div>
        ))}

        <div className="col-span-1 md:col-span-2 flex justify-center">
          <button type="submit" className="bg-[#424752] rounded-lg hover:bg-customBlue-dark text-white font-bold py-2 px-8 shadow-md focus:outline-none focus:ring-4 focus:ring-customBlue-light transition duration-200 ease-in-out">
            Submit
          </button>
        </div>
      </form>
    </div>
  );
};

export default Form;
